/**
 * CetusEd Admin Console — Root App Component
 *
 * COMPLIANCE: All routes require admin authentication. The admin auth
 *   provider enforces JWT validation with MFA claims. Cross-district
 *   impersonation is gated by explicit RBAC capability 'admin:impersonate:*'.
 *
 * MIGRATION TARGET: New application for the vendor-hosted admin layer.
 *   Route structure defined per CP-R1325.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminAuthProvider } from './auth/AdminAuthProvider';
import { LoginPage } from './auth/LoginPage';
import { ProtectedRoute } from './ui/Layout';
import { Layout } from './ui/Layout';
import { DistrictList } from './features/districts/DistrictList';
import { DistrictDetail } from './features/districts/DistrictDetail';
import { DistrictProvision } from './features/districts/DistrictProvision';
import { DistrictAudit } from './features/districts/DistrictAudit';
import { BillingDashboard } from './features/billing/BillingDashboard';
import { InvoiceList } from './features/billing/InvoiceList';
import { PlanManagement } from './features/billing/PlanManagement';
import { Dashboard } from './features/monitoring/Dashboard';
import { Alerts } from './features/monitoring/Alerts';
import { AuditLog } from './features/monitoring/AuditLog';
import { RateLimits } from './features/monitoring/RateLimits';
import { SSOConfig } from './features/integrations/SSOConfig';
import { SISConfig } from './features/integrations/SISConfig';
import { WebhookConfig } from './features/integrations/WebhookConfig';
import { EmailConfig } from './features/integrations/EmailConfig';
import { TicketList } from './features/support/TicketList';
import { Impersonate } from './features/support/Impersonate';
import { Announcements } from './features/support/Announcements';

export const App: React.FC = () => {
  return (
    <AdminAuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<Layout />}>
            <Route path="/districts" element={<DistrictList />} />
            <Route path="/districts/:districtId" element={<DistrictDetail />} />
            <Route path="/districts/provision" element={<DistrictProvision />} />
            <Route path="/districts/:districtId/audit" element={<DistrictAudit />} />
            <Route path="/billing" element={<BillingDashboard />} />
            <Route path="/billing/invoices" element={<InvoiceList />} />
            <Route path="/billing/plans" element={<PlanManagement />} />
            <Route path="/monitoring" element={<Dashboard />} />
            <Route path="/monitoring/alerts" element={<Alerts />} />
            <Route path="/monitoring/audit-log" element={<AuditLog />} />
            <Route path="/monitoring/rate-limits" element={<RateLimits />} />
            <Route path="/integrations/sso" element={<SSOConfig />} />
            <Route path="/integrations/sis" element={<SISConfig />} />
            <Route path="/integrations/webhooks" element={<WebhookConfig />} />
            <Route path="/integrations/email" element={<EmailConfig />} />
            <Route path="/support/tickets" element={<TicketList />} />
            <Route path="/support/impersonate" element={<Impersonate />} />
            <Route path="/support/announcements" element={<Announcements />} />
            <Route path="/" element={<Navigate to="/districts" replace />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </AdminAuthProvider>
  );
};
